import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiHome, FiUser, FiArrowRight, FiShield } from 'react-icons/fi';
import { initials } from '../Header';
import Dropdown from '../Dropdown';
import { toast } from '../Toast';
import { SettingsHeader, SettingsActions, RecordBanner, useSettingsSub } from './ClientSettings';
import { NOT_CONNECTED, PEOPLE, useClient } from './clientSettingsData';

const TIME_ZONES = [
    { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
    { value: 'Australia/Darwin', label: 'Australia/Darwin (ACST)' },
    { value: 'Australia/Adelaide', label: 'Australia/Adelaide (ACST/ACDT)' },
    { value: 'Australia/Brisbane', label: 'Australia/Brisbane (AEST)' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST/AEDT)' },
    { value: 'Australia/Hobart', label: 'Australia/Hobart (AEST/AEDT)' },
];
const YEAR_ENDS = ['30 June', '31 December', '31 March', '30 September'].map(x => ({ value: x, label: x }));

interface Form {
    website: string;
    timeZone: string;
    yearEnd: string;
    // null = no logo; a blob: url while a new one is only previewed
    logo: string | null;
}

const Company: React.FC = () => {
    const history = useHistory();
    const { stock, name, ticker, exchange } = useClient();
    const sub = useSettingsSub();
    const initial: Form = { website: stock?.websiteUrl ?? '', timeZone: TIME_ZONES[0].value, yearEnd: YEAR_ENDS[0].value, logo: stock?.companyLogoUrl ?? null };
    const [form, setForm] = useState<Form>(initial);
    const [saved, setSaved] = useState<Form>(initial);
    const fileRef = useRef<HTMLInputElement>(null);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const set = (field: keyof Form, value: string | null) => setForm(f => ({ ...f, [field]: value }));

    const pickLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!['image/png', 'image/svg+xml'].includes(file.type)) {
            toast.error('Choose a PNG or SVG');
            return;
        }
        set('logo', URL.createObjectURL(file));
    };

    const save = () => {
        setSaved(form);
        toast.info('Preview only: this isn’t connected yet, so the company details were not saved');
    };

    const actions = { dirty, onSave: save, onDiscard: () => setForm(saved) };
    const people = PEOPLE(name);

    return <>
        <SettingsHeader title="Company Details" sub={sub} {...actions} />
        <RecordBanner />

        <div className="cs-row">
            <div className="cs-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiHome /></span>
                        <div className="text">
                            <h2>Company Details</h2>
                            <div className="hint">Shown on the Website Hub and in emails</div>
                        </div>
                    </div>

                    <div className="logo-row">
                        {form.logo
                            ? <img className="logo" src={form.logo} alt={`${name} logo`} />
                            : <span className="logo mark">{initials(name)}</span>}
                        <div>
                            <div className="label">Company logo</div>
                            <div className="hint">PNG or SVG, at least 256 × 256 px</div>
                            <div className="actions">
                                <button type="button" className="btn-outline" onClick={() => fileRef.current?.click()}>Upload New</button>
                                {form.logo && <button type="button" className="remove" onClick={() => set('logo', null)}>Remove</button>}
                                <input ref={fileRef} type="file" accept="image/png,image/svg+xml" hidden onChange={pickLogo} />
                            </div>
                        </div>
                    </div>

                    <div className="fields identity">
                        <div className="field">
                            <span className="label">Company name <em>· managed by Investi</em></span>
                            <div className="value-box locked"><span>{name}</span><FiShield /></div>
                        </div>
                        <div className="field">
                            <span className="label">Ticker <em>· managed by Investi</em></span>
                            <div className="value-box locked"><span>{ticker}</span><FiShield /></div>
                        </div>
                        <div className="field">
                            <span className="label">Exchange <em>· managed by Investi</em></span>
                            <div className="value-box locked"><span>{exchange}</span><FiShield /></div>
                        </div>
                    </div>

                    <div className="fields">
                        <label className="field">
                            <span className="label">Website</span>
                            <input type="url" value={form.website} placeholder="https://" onChange={e => set('website', e.target.value)} />
                        </label>
                        <div className="field">
                            <span className="label">Website Hub address</span>
                            <div className="value-box">
                                <span>investors.{(form.website || 'yourcompany.com.au').replace(/^https?:\/\/(www\.)?/, '').replace(/\/.*$/, '')}</span>
                                <button type="button" className="link" onClick={() => toast.info(NOT_CONNECTED)}>Change</button>
                            </div>
                        </div>
                        <div className="field">
                            <span className="label">Time zone</span>
                            <Dropdown value={form.timeZone} options={TIME_ZONES} onChange={v => set('timeZone', v)} />
                        </div>
                        <div className="field">
                            <span className="label">Financial year end</span>
                            <Dropdown value={form.yearEnd} options={YEAR_ENDS} onChange={v => set('yearEnd', v)} />
                        </div>
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiUser /></span><h2>Who Can Access</h2>
                        <button type="button" className="link in-title" onClick={() => history.push('/client-settings/users')}>Manage <FiArrowRight /></button>
                    </div>
                    {people.map(p => <div key={p.id} className="person">
                        <span className="av">{initials(p.name)}</span>
                        <div className="text">
                            <strong>{p.name}</strong>
                            <span>{p.organisation} · {p.role}</span>
                        </div>
                    </div>)}
                    <button type="button" className="link at-bottom" onClick={() => history.push('/client-settings/users')}>Manage <FiArrowRight /></button>
                </section>
            </div>
        </div>

        <div className="action-bottom"><SettingsActions {...actions} /></div>
    </>;
};

export default Company;
